import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useCart } from "../contexts/cartContext";
import { createCheckoutSession } from "../api/checkout";
import { capFirst } from "../utils/groupVariants";

interface Props {
  open: boolean;
  onClose: () => void;
}

const CartDrawer = ({ open, onClose }: Props) => {
  const { items, cartTotal, cartCount, removeFromCart } = useCart();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  const handleCheckout = async () => {
    setCheckoutError("");
    setCheckoutLoading(true);
    try {
      const { url } = await createCheckoutSession(items);
      sessionStorage.setItem("checkout_pending", "true");
      window.location.href = url;
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : "Checkout failed");
      setCheckoutLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: { sx: { width: { xs: "100vw", sm: 400 }, bgcolor: "white" } },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: "1px solid #e5e5e5",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
            }}
          >
            Cart {cartCount > 0 && `(${cartCount})`}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {items.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              color: "#737373",
            }}
          >
            <Typography variant="body2" sx={{ letterSpacing: "0.05em" }}>
              Your cart is empty
            </Typography>
            <Button
              onClick={onClose}
              sx={{
                color: "black",
                fontSize: "0.72rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                textDecoration: "underline",
                "&:hover": { bgcolor: "transparent" },
              }}
            >
              Continue Shopping
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
              {items.map((item) => {
                const price = item.variant.salePrice ?? 0;
                return (
                  <Box
                    key={item.variant.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "72px 1fr auto",
                      gap: 2,
                      py: 2.5,
                      borderBottom: "1px solid #f5f5f5",
                    }}
                  >
                    <Box
                      component="img"
                      src={item.variant.image}
                      alt={item.variant.name}
                      sx={{
                        width: 72,
                        height: 90,
                        objectFit: "cover",
                        bgcolor: "#f5f5f5",
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, mt: 0.25, lineHeight: 1.3 }}
                      >
                        {capFirst(item.variant.name)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#737373", mt: 0.5, display: "block" }}
                      >
                        Size: {item.variant.size}
                      </Typography>
                      {item.quantity > 1 && (
                        <Typography variant="caption" sx={{ color: "#737373" }}>
                          Qty: {item.quantity}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mt: 0.5 }}
                      >
                        ${(price * item.quantity).toFixed(2)}
                      </Typography>
                    </Box>
                    <IconButton
                      aria-label="Remove item"
                      onClick={() => removeFromCart(item.variant.id)}
                      size="small"
                      sx={{
                        color: "#a3a3a3",
                        alignSelf: "flex-start",
                        mt: -0.5,
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ px: 3, py: 3, borderTop: "1px solid #e5e5e5" }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    fontWeight: 600,
                  }}
                >
                  Subtotal
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  ${cartTotal.toFixed(2)}
                </Typography>
              </Box>
              {checkoutError && (
                <Alert
                  severity="error"
                  sx={{ mb: 1.5, borderRadius: 0, fontSize: "0.78rem" }}
                >
                  {checkoutError}
                </Alert>
              )}
              <Button
                variant="contained"
                fullWidth
                disabled={checkoutLoading}
                onClick={handleCheckout}
                sx={{
                  bgcolor: "black",
                  color: "white",
                  py: 1.75,
                  fontSize: "0.75rem",
                  letterSpacing: "0.12em",
                  "&:hover": { bgcolor: "#333" },
                }}
              >
                {checkoutLoading ? (
                  <CircularProgress size={18} sx={{ color: "white" }} />
                ) : (
                  "Checkout"
                )}
              </Button>
              <Divider sx={{ my: 2 }} />
              <Button
                onClick={onClose}
                fullWidth
                sx={{
                  color: "#737373",
                  fontSize: "0.72rem",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  "&:hover": { color: "black", bgcolor: "transparent" },
                }}
              >
                Continue Shopping
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default CartDrawer;
